import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, RefreshCw, Settings, Package, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProdigiConfig = () => {
  const navigate = useNavigate();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    try {
      const [testRes, catalogRes] = await Promise.all([
        axios.get(`${API}/prodigi/test`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prodigi/catalog`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setConnectionStatus(testRes.data);
      setCatalog(catalogRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load Prodigi configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.get(`${API}/prodigi/test`, { headers: { Authorization: `Bearer ${token}` } });
      setConnectionStatus(res.data);
      toast.success(res.data.success ? 'Connected to Prodigi!' : 'Connection failed');
    } catch {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const paperLabels = {
    matte: 'Enhanced Matte Art',
    hahnemuhle: 'Hahnemühle Photo Rag',
    canvas: 'Stretched Canvas',
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="prodigi-config-page">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <button onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors"
          data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-8 sm:mb-12"
          data-testid="prodigi-config-title">
          Prodigi Integration
        </h1>

        {/* Connection Status Card */}
        <div className="bg-card border border-border rounded-lg p-5 sm:p-6 mb-8" data-testid="prodigi-connection-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${connectionStatus?.success ? 'bg-green-500' : loading ? 'bg-gray-400 animate-pulse' : 'bg-red-500'}`} />
              <div>
                <h3 className="font-sans text-sm font-semibold uppercase tracking-[0.1em]">API Connection</h3>
                {loading ? (
                  <p className="font-sans text-sm text-muted-foreground">Testing connection...</p>
                ) : connectionStatus?.success ? (
                  <p className="font-sans text-sm text-muted-foreground">
                    Connected to <span className="font-medium text-foreground">Prodigi API (v4.0)</span>
                  </p>
                ) : (
                  <p className="font-sans text-sm text-red-600">
                    {connectionStatus?.error || 'Not connected'}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleTestConnection} disabled={testing}
              data-testid="test-connection-btn">
              <RefreshCw size={14} className={`mr-1.5 ${testing ? 'animate-spin' : ''}`} />
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-5 sm:p-6 mb-8">
          <h3 className="font-sans text-sm font-semibold uppercase tracking-[0.1em] text-accent mb-3 flex items-center gap-2">
            <Settings size={16} /> How International Print Fulfillment Works
          </h3>
          <ol className="font-sans text-sm text-muted-foreground space-y-2 list-decimal list-inside">
            <li>Customer configures a print (size, paper, frame) and pays via Razorpay</li>
            <li>Order goes to <strong>Admin Approval Queue</strong> for review</li>
            <li>Admin approves — order routes to Prodigi for museum-grade Giclée printing</li>
            <li>Prodigi prints on archival paper/canvas, frames (if selected), and ships worldwide</li>
            <li>Order status updates appear in your Order Management dashboard</li>
          </ol>
        </div>

        {/* Paper Types */}
        {catalog?.papers && (
          <div className="space-y-6 mb-8">
            <h2 className="font-serif text-xl sm:text-2xl font-light">Available Papers</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {Object.entries(catalog.papers).map(([key, paper]) => (
                <div key={key} className="bg-card border border-border rounded-lg p-5">
                  <h3 className="font-sans text-sm font-semibold mb-1">{paper.label}</h3>
                  <p className="font-sans text-xs text-muted-foreground">{paper.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Frame Options */}
        {catalog?.frames && (
          <div className="space-y-6 mb-8">
            <h2 className="font-serif text-xl sm:text-2xl font-light">Frame Options</h2>
            <div className="grid sm:grid-cols-4 gap-4">
              {Object.entries(catalog.frames).map(([key, frame]) => (
                <div key={key} className="bg-card border border-border rounded-lg p-5">
                  <h3 className="font-sans text-sm font-semibold mb-1">{frame.label}</h3>
                  <p className="font-sans text-xs text-muted-foreground">{frame.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SKU Mappings */}
        {catalog?.sku_map && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl sm:text-2xl font-light">SKU Mappings</h2>
              <span className="font-sans text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                {Object.keys(catalog.sku_map).length} sizes configured
              </span>
            </div>
            <p className="font-sans text-sm text-muted-foreground">
              Each print size maps to specific Prodigi product SKUs for Giclée fine art prints and stretched canvas.
            </p>

            {['matte', 'hahnemuhle', 'canvas'].map(paper => (
              <div key={paper} className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/30 px-5 py-3 border-b border-border">
                  <h3 className="font-sans text-sm font-semibold uppercase tracking-[0.1em]">
                    <Package size={14} className="inline mr-2 text-accent" />
                    {paperLabels[paper]}
                  </h3>
                </div>
                <div className="divide-y divide-border">
                  {Object.entries(catalog.sku_map)
                    .filter(([, skus]) => skus[paper])
                    .map(([size, skus]) => {
                      const sizeInfo = catalog.sizes?.[size];
                      return (
                        <div key={`${size}-${paper}`} className="flex items-center gap-4 px-5 py-3 text-sm" data-testid={`sku-${size}-${paper}`}>
                          <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded w-16 text-center flex-shrink-0">{sizeInfo?.label || size}</span>
                          <span className="font-sans text-muted-foreground w-20 flex-shrink-0 capitalize">{sizeInfo?.orientation}</span>
                          <span className="font-sans text-muted-foreground mx-1">&rarr;</span>
                          <span className="font-mono text-xs font-medium flex-1">{skus[paper]}</span>
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProdigiConfig;
